import type { IBankAccount } from '@commenda/api/bankAccount';
import type { IIndirectTaxFilingV2LiabilityReportFiling } from '@commenda/api/indirect-tax/filings';
import { IIndirectTaxFilingV2ServiceRequestAssociation } from '@commenda/api/indirect-tax/filings/types';
import { IndirectTaxJurisdictionType } from '@commenda/api/indirect-tax/registrations/types';
import { FileBasic } from '@commenda/types/src/file/types';
import React from 'react';

import { IndirectTaxRegion } from '../../common';

export { IndirectTaxJurisdictionType };

export enum IndirectTaxFilingStatus {
  ACTIVE = 'ACTIVE',
  FILED = 'FILED',
  SCHEDULED = 'SCHEDULED',
  ACCRUING = 'ACCRUING',
  OVERDUE = 'OVERDUE',
}

export enum IndirectTaxFilingTableFilter {
  JURISDICTION = 'JURISDICTION',
  FILING_FREQUENCY = 'FILING_FREQUENCY',
  DUE_DATE = 'dueDate',
}

export interface IIndirectTaxFiling {
  id: string;
  returnName: string;
  status: IndirectTaxFilingStatus;
  jurisdiction: string;
  countryCode: string;
  filingPeriod: string;
  filingFrequency: string;
  dueDate: string;
  amount: number | null;
  currency: string;
  region: IndirectTaxRegion;
  hasGeneratedFilingReport?: boolean;
  canApprove: boolean;
  canReview: boolean;
  filedByAgentName: string | null;

  taxableSales?: number;
  nonTaxableSales?: number;
  exemptSales?: number;
  taxDue?: number;
  totalSales?: number;
  taxablePurchases?: number;
  intraCommunitySales?: number;
  intraCommunityPurchases?: number;
  vatPosition?: number;
}

export interface IIndirectTaxFilingSummaryConfig {
  label: string;
  key: keyof IIndirectTaxFiling;
  isCurrency: boolean;
}

export interface IIndirectTaxFilingStatusBadgeConfig {
  label: string;
  variant: 'blue' | 'success' | 'warning' | 'gray' | 'error';
}

export interface IIndirectTaxFilingStatusStyles {
  text?: string;
  count?: string;
}

export enum IndirectTaxFilingTaskStatus {
  PENDING = 'PENDING',
  NO_TASKS = 'NO_TASKS',
  COMPLETED = 'COMPLETED',
}

// Client-facing actions that can fire from the filing detail task table.
// Today only REVIEW_TRANSACTIONS (approve) is client-actionable; submission is
// agent-only. Extend this enum if new client-turn task types are added.
export enum IndirectTaxFilingClientActionType {
  APPROVE = 'APPROVE',
}

export interface IIndirectTaxFilingTask {
  id: string;
  title: string;
  description: string;
  status: IndirectTaxFilingTaskStatus;
  actionType: IndirectTaxFilingClientActionType;
}

export interface IIndirectTaxFilingReturnSummaryRow {
  label: string;
  value: number | null;
}

export interface IIndirectTaxFilingReturnSummarySection {
  title: string;
  rows: IIndirectTaxFilingReturnSummaryRow[];
}

// Contribution summaries may have no calculated amount. Flat API fields omit
// zero amounts, so their summaries normalize absent values to zero.
export interface IIndirectTaxFilingReturnSummary {
  sections: IIndirectTaxFilingReturnSummarySection[];
  currency: string;
  amountDue: number | null;
}

export type IIndirectTaxFilingDocument = FileBasic & {
  createdAt?: number;
};

export interface IIndirectTaxFilingDetail extends IIndirectTaxFiling {
  tasks: IIndirectTaxFilingTask[];
  serviceRequest?: IIndirectTaxFilingV2ServiceRequestAssociation | null;
  remittanceAccount: IBankAccount | null;
  filingConfirmationDocuments: IIndirectTaxFilingDocument[];
  relatedDocuments: IIndirectTaxFilingDocument[];
  amountDue: number | null;
  transactionCount: number;
  filedOn: string;
  entityName: string;
  entityCountryCode: string;
  filingLockedAt?: string | Date | null;
  reportingPeriodStartDate: string;
  reportingPeriodEndDate: string;
  jurisdictionType: IndirectTaxJurisdictionType;
  jurisdictionId: string;
  registrationTaxTypeId: string;
}

export interface IIndirectTaxFilingColumn {
  accessorKey: string;
  header: string;
  title: string;
  showInSelector?: boolean;
  enableSorting?: boolean;
  align?: 'left' | 'center' | 'right';
  width?: number;
  headerClassName?: string;
  sort?: {
    shouldSort: boolean;
    sortingFn: (a: string, b: string) => number;
    ui: {
      asc: { columnNode: string; globalNode: string };
      desc: { columnNode: string; globalNode: string };
    };
  };
  cell: (info: {
    row: {
      original: IIndirectTaxFiling;
      getValue: (key: string) => unknown;
    };
  }) => React.ReactNode;
}

export interface IIndirectTaxFilingColumnGroup {
  id: string;
  sticky?: 'left' | 'right';
  columns: IIndirectTaxFilingColumn[];
}

/** Tax liability report — row for filings with sales/financial breakdown. */
export interface BreakdownLiabilityReportTableRow {
  filingId: string;
  jurisdiction: string;
  formTitle: string;
  filingPeriodLabel: string;
  dueDateLabel: string;
  transactionsCount: number;
  taxableAmount: number;
  nonTaxableAmount: number;
  taxDue: number;
  filingStatusUI: IndirectTaxFilingStatus;
  currency: string;
}

/** Tax liability report — row for filings with VAT box contributions. */
export interface ContributionLiabilityReportTableRow {
  filingId: string;
  jurisdiction: string;
  formTitle: string;
  taxPeriodLabel: string;
  dueDateLabel: string;
  filingStatusUI: IndirectTaxFilingStatus;
  amountDue: number;
  currency: string;
  contributions: IIndirectTaxFilingV2LiabilityReportFiling['contributions'];
}

export type IndirectTaxFilingsStatusRowSource =
  | { type: 'job'; jobId: string }
  | { type: 'filing'; filingId: string };
